import { toast } from 'sonner';
import { Heart, Copy } from 'lucide-react';
import { createElement } from 'react';

// Round 71i (Feb 2026): post-export referral nudge.
//
// Surfaces a friendly toast right after a user prints or downloads a
// tax-prep report. Tax season is the only time of the year dividend
// investors actively share their tools with each other — the toast
// rides the dopamine of just having generated a useful document and
// turns it into one of the highest-leverage acquisition moments
// DiviDrip has all year.
//
// The "referral link" is just `https://twylightcrow.com/?ref=USERID`
// for now — there's no backend referral attribution system yet but
// the param will start showing up in analytics so we can wire
// proper credit later without changing the front-door UX.
//
// Falls back gracefully when:
//   • clipboard API is blocked (private mode / iframes) → fallback to
//     execCommand('copy')
//   • No user object available → toast still shows but the link goes
//     to the marketing root (no ?ref param)
//
// Caller passes `kind` ('year-end' or 'harvest') so the toast copy
// matches the document the user just generated.

const PROD_HOST = 'https://twylightcrow.com';

function buildReferralUrl(user) {
  const ref = user?.user_id || user?.email || '';
  if (!ref) return PROD_HOST + '/';
  // user_id values like "user_57a7383fc018" are short and URL-safe;
  // emails are not, so we hash to a short cleaner string. encodeURIComponent
  // keeps it safe either way.
  return `${PROD_HOST}/?ref=${encodeURIComponent(ref)}`;
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

const COPY = {
  'year-end': {
    title: 'Liked your Year-End Snapshot? 💚',
    body: 'Send a friend your DiviDrip referral link — they get the same tax-prep tools instantly.',
  },
  'harvest': {
    title: 'Got your Tax-Harvest Report? 💚',
    body: 'Share DiviDrip with a fellow dividend investor — referral links are the kindest tax-season hand-off.',
  },
};

export function showReferralToast({ kind = 'year-end', user, delayMs = 1200 } = {}) {
  // Tiny delay so the print dialog / download notification gets out of
  // the way first. Otherwise the toast fires UNDER the browser's
  // native download notification and the user never sees it.
  const url = buildReferralUrl(user);
  const text = COPY[kind] || COPY['year-end'];
  const fire = () => {
    toast.custom(
      (t) =>
        createElement(
          'div',
          {
            className:
              'flex items-start gap-3 rounded-xl border-2 border-rose-300/70 dark:border-rose-700/70 bg-gradient-to-br from-rose-50 to-amber-50 dark:from-rose-950/60 dark:to-amber-950/30 p-4 shadow-2xl max-w-md',
            'data-testid': `referral-toast-${kind}`,
          },
          createElement(Heart, {
            key: 'icon',
            className: 'w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5',
            fill: 'currentColor',
          }),
          createElement(
            'div',
            { key: 'body', className: 'flex-1 min-w-0' },
            createElement(
              'div',
              { key: 'title', className: 'text-sm font-bold text-rose-800 dark:text-rose-200' },
              text.title
            ),
            createElement(
              'p',
              { key: 'p', className: 'text-xs text-foreground/80 mt-1 leading-relaxed' },
              text.body
            ),
            createElement(
              'div',
              { key: 'actions', className: 'flex flex-wrap items-center gap-2 mt-3' },
              createElement(
                'button',
                {
                  key: 'copy',
                  type: 'button',
                  onClick: async () => {
                    const ok = await copyToClipboard(url);
                    if (ok) {
                      toast.success('Referral link copied!', {
                        description: 'Paste it in a DM, email, or your dividend-investor group chat.',
                      });
                    } else {
                      toast.error('Couldn\'t copy automatically', {
                        description: `Long-press to copy: ${url}`,
                      });
                    }
                    toast.dismiss(t);
                  },
                  className:
                    'inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-colors',
                  'data-testid': 'referral-copy-link-btn',
                },
                createElement(Copy, { key: 'i', className: 'w-3 h-3' }),
                'Copy referral link'
              ),
              createElement(
                'button',
                {
                  key: 'dismiss',
                  type: 'button',
                  onClick: () => toast.dismiss(t),
                  // Light theme: transparent until hover (clean look against
                  // the rose/amber gradient). Dark theme: a baseline slate
                  // pill so the text doesn't disappear into the page words
                  // bleeding through the semi-transparent toast — hover then
                  // deepens it into a more prominent button.
                  className:
                    'inline-flex items-center px-2.5 h-8 rounded-lg text-xs font-semibold text-muted-foreground hover:text-foreground bg-transparent dark:bg-slate-800/70 hover:bg-muted/40 dark:hover:bg-slate-700 dark:hover:text-white transition-colors',
                  'data-testid': 'referral-dismiss-btn',
                },
                'Maybe later'
              )
            )
          )
        ),
      { duration: 12000 }
    );
  };
  if (delayMs > 0) setTimeout(fire, delayMs);
  else fire();
}
